
/**
* Return the first non-transparent background colour the browser finds
* while walking up the DOM from `el`.  Falls back to white.
*/
function findOpaqueBg(el) {
    while (el && el !== document.documentElement) {
        const bg = getComputedStyle(el).backgroundColor;
        // Chrome/Firefox report rgba(0,0,0,0); Safari reports 'transparent'
        if (bg && bg !== 'transparent' && !bg.endsWith(', 0)')) {
            return bg;
        }
        el = el.parentElement;
    }
    return '#fff';
}

/**
 * Make sticky cells opaque for both 1- and 2-column modes on left/right.
 * Works with: stick-left, stick-left-2, stick-right, stick-right-2
 */
function solidifyStickyCells(root = document) {
    const tables = root.querySelectorAll('.table-component');

    for (const table of tables) {
        const cls = table.className;
        const leftCount  = /\bstick-left-(\d+)\b/.exec(cls)?.[1] ? parseInt(/\bstick-left-(\d+)\b/.exec(cls)[1], 10)
            : (/\bstick-left\b/.test(cls) ? 1 : 0);
        const rightCount = /\bstick-right-(\d+)\b/.exec(cls)?.[1] ? parseInt(/\bstick-right-(\d+)\b/.exec(cls)[1], 10)
            : (/\bstick-right\b/.test(cls) ? 1 : 0);

        if (!leftCount && !rightCount) continue;

        // thead rows + tbody rows
        const allRows = [
            ...(table.tHead ? Array.from(table.tHead.rows) : []),
            ...Array.from(table.tBodies || []).flatMap(tb => Array.from(tb.rows))
        ];

        for (const row of allRows) {
            const cells = Array.from(row.cells);
            if (!cells.length) continue;

            // LEFT stickies: indices 0..leftCount-1
            for (let i = 0; i < leftCount; i++) {
                const cell = cells[i];
                if (!cell) continue;
                const own = getComputedStyle(cell).backgroundColor;
                if (own === 'transparent' || own.endsWith(', 0)')) {
                    cell.style.backgroundColor = findOpaqueBg(cell);
                }
            }

            // RIGHT stickies: last, last-1, ...
            for (let k = 0; k < rightCount; k++) {
                const idx = cells.length - 1 - k;
                if (idx < 0) break;
                const cell = cells[idx];
                const own = getComputedStyle(cell).backgroundColor;
                if (own === 'transparent' || own.endsWith(', 0)')) {
                    cell.style.backgroundColor = findOpaqueBg(cell);
                }
            }
        }
    }
}

/* run once after the page is ready */
document.addEventListener('DOMContentLoaded', () => solidifyStickyCells());

/* OPTIONAL: if your tables are added/updated dynamically, put a
MutationObserver on the container and re-run `solidifyStickyCells()` */

(function () {
    const DEBOUNCE_MS = 50;

    function getCountsFromClasses(table) {
        const cls = table.className;
        const left = /\bstick-left-(\d+)\b/.exec(cls)?.[1] ?? (/\bstick-left\b/.test(cls) ? '1' : '0');
        const right = /\bstick-right-(\d+)\b/.exec(cls)?.[1] ?? (/\bstick-right\b/.test(cls) ? '1' : '0');
        return { left: parseInt(left, 10), right: parseInt(right, 10) };
    }

    function getHeaderRow(table) {
        // Prefer THEAD first row; fall back to first body row
        if (table.tHead && table.tHead.rows.length) return table.tHead.rows[0];
        const firstBodyRow = table.tBodies?.[0]?.rows?.[0];
        return firstBodyRow || null;
    }

    function updateStickyOffsets(table) {
        const { left, right } = getCountsFromClasses(table);
        if (!left && !right) return;

        const headerRow = getHeaderRow(table);
        if (!headerRow) return;

        const cells = Array.from(headerRow.cells);
        if (!cells.length) return;

        // Compute LEFT offsets: sum of widths of preceding sticky columns
        let accLeft = 0;
        for (let i = 0; i < left; i++) {
            const width = cells[i]?.getBoundingClientRect().width || 0;
            // offset for column i+1 is the sum BEFORE this column
            table.style.setProperty(`--stick-left-${i+1}`, accLeft + 'px');
            accLeft += width;
        }

        // Compute RIGHT offsets: sum of widths of trailing sticky columns (from the end)
        let accRight = 0;
        for (let k = 0; k < right; k++) {
            const idxFromEnd = cells.length - 1 - k; // last, last-1, ...
            const width = cells[idxFromEnd]?.getBoundingClientRect().width || 0;
            table.style.setProperty(`--stick-right-${k+1}`, accRight + 'px');
            accRight += width;
        }
    }

    // Debounce to avoid thrashing on resize
    function debounce(fn, ms) {
        let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
    }
    const onResize = debounce(() => {
        document.querySelectorAll('.table-component').forEach(updateStickyOffsets);
    }, DEBOUNCE_MS);

    // Init on ready + whenever fonts/layout settle
    function init() {
        document.querySelectorAll('.table-component').forEach(updateStickyOffsets);
    }

    // Recalc when columns resize (optional but nice)
    const ro = new ResizeObserver(entries => {
        const tables = new Set();
        for (const e of entries) {
            const table = e.target.closest('.table-component');
            if (table) tables.add(table);
        }
        tables.forEach(updateStickyOffsets);
    });

    // Observe all header rows for size changes
    function attachObservers() {
        document.querySelectorAll('.table-component').forEach(table => {
            const headerRow = getHeaderRow(table);
            if (headerRow) ro.observe(headerRow);
        });
    }

    window.addEventListener('DOMContentLoaded', () => {
        init();
        attachObservers();
        window.addEventListener('resize', onResize);
    });
})();

